import React, { useState, useEffect } from 'react';
import { Button, CircularProgress } from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { followUser, unfollowUser, isFollowing } from '../../services/followService';

interface FollowButtonProps {
  targetUserId: string;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  onFollowChange?: (isFollowing: boolean) => void;
}

export const FollowButton: React.FC<FollowButtonProps> = ({
  targetUserId,
  variant = 'contained',
  size = 'small',
  onFollowChange,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkFollowing = async () => {
      if (!user?.uid || !targetUserId) {
        setChecking(false);
        return;
      }

      if (user.uid === targetUserId) {
        setChecking(false);
        return;
      }

      try {
        const isFollowingUser = await isFollowing(user.uid, targetUserId);
        setFollowing(isFollowingUser);
      } catch (error) {
        console.error('Erro ao verificar se está seguindo:', error);
      } finally {
        setChecking(false);
      }
    };

    checkFollowing();
  }, [user?.uid, targetUserId]);

  const handleToggleFollow = async () => {
    if (!user?.uid) return;

    setLoading(true);
    try {
      if (following) {
        await unfollowUser(user.uid, targetUserId);
        setFollowing(false);
        onFollowChange?.(false);
      } else {
        await followUser(user.uid, targetUserId);
        setFollowing(true);
        onFollowChange?.(true);
      }
    } catch (error) {
      console.error('Erro ao alterar follow:', error);
    } finally {
      setLoading(false);
    }
  };

  // Não mostrar botão para o próprio usuário
  if (user?.uid === targetUserId) {
    return null;
  }

  if (checking) {
    return (
      <Button
        variant={variant}
        size={size}
        disabled
        sx={{
          minWidth: 100,
        }}
      >
        <CircularProgress size={16} />
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      disabled={loading}
      onClick={handleToggleFollow}
      startIcon={
        loading ? (
          <CircularProgress size={16} />
        ) : following ? (
          <PersonRemoveIcon />
        ) : (
          <PersonAddIcon />
        )
      }
      sx={{
        minWidth: 100,
        backgroundColor: following
          ? 'transparent'
          : variant === 'contained'
          ? theme.colors.primary
          : 'transparent',
        color: following
          ? theme.colors.text.secondary
          : variant === 'contained'
          ? theme.colors.text.contrast
          : theme.colors.primary,
        borderColor: following ? theme.colors.border : theme.colors.primary,
        '&:hover': {
          backgroundColor: following
            ? `${theme.colors.border}30`
            : variant === 'contained'
            ? theme.colors.primary
            : `${theme.colors.primary}10`,
          opacity: variant === 'contained' ? 0.9 : 1,
        },
      }}
    >
      {following ? 'Seguindo' : 'Seguir'}
    </Button>
  );
};
